import { CategoryMagazineCount, MagazineState, RootState } from '@/store/storeStateInterfaces';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import MagazineRepository from '@/api/repositories/MagazineRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Magazine from '@/models/Magazine';
import Category from '@/models/Category';
import MagazineImage from '@/models/MagazineImage';
import i18n from '@/i18n';

const magazineRepository: MagazineRepository = RepositoryFactory.get('magazine');

// create default category
const defaultCategory = new Category();
defaultCategory.id = process.env.VUE_APP_DEFAULT_CATEGORY_ID!;
defaultCategory.name = process.env.VUE_APP_DEFAULT_CATEGORY_NAME!;

const store: MagazineState = {
    pagination: null,
    paginationIndex: 0,
    isLoadingMagazines: false,
    isInitialLoadingDone: false,
    magazines: [],
    filteredMagazines: [],
    paginatedMagazines: [],
    categories: [],
    categoryMagazineCount: {},
    selectedMagazine: undefined,
    languages: [],
    filter: {
        searchText: '',
        category: defaultCategory,
        languages: [process.env.VUE_APP_DEFAULT_MAGAZINE_LANGUAGE!]
    },
    images: []
};

const actions: ActionTree<MagazineState, RootState> = {
    getLocationMagazines: async ({ commit, getters }, locationId?: string): Promise<Magazine[]> => {
        try {
            commit('setLoadingMagazines', true);
            const response = await magazineRepository.getLocationMagazines(locationId);
            const magazines: Magazine[] = Magazine.parseFromArray(response.data.response) as Magazine[];
            commit('storeMagazines', magazines);
            // extract information which depend from magazines
            commit('setLanguages', magazines);
            // -- set category magazine count
            commit('setCategoryMagazineCount', magazines);
            // apply saved filter values
            commit('applyFilter');
            if (getters.pagination) {
                commit('applyPagination', 0);
            }
            commit('setLoadingMagazines', false);
            commit('setIsInitialLoadingDone', true);
            return magazines;
        } catch (e) {
            // this is necessary to stop loading magazines loading animation
            commit('setLoadingMagazines', false);
            throw e;
        }
    },
    getCategories: async ({ commit }): Promise<Category[]> => {
        const response = await magazineRepository.getCategoriesByLocale(i18n.locale);
        const categories: Category[] = Category.parseFromArray(response.data.response) as Category[];
        // all categories that should should be in categories first (order is respected)
        // the "all" category is added in category dialog component as first category anywas
        const preferredCategories = [
            process.env.VUE_APP_DEFAULT_CATEGORY_ID!,
            '13' // 13 means newspaper
        ];
        // take the preferred categories, determine the rest, sort the rest alphabetically
        // and create new array
        const preferred = preferredCategories
            .map(preferredItem => categories.find(item => item.id === preferredItem)!);
        const sortedRest = categories
            .filter(item => !preferredCategories.includes(item.id))
            .sort((a: Category, b: Category) => a.name.localeCompare(b.name, i18n.locale));

        const sortedCategories = [...preferred, ...sortedRest];
        commit('storeCategories', sortedCategories);
        return sortedCategories;
    },
    getMagazineThumbnail: async ({ commit }, payload: { publicationId: string, page: number }): Promise<string> => {
        const response = await magazineRepository.getMagazineThumbnail(payload);
        return URL.createObjectURL(response.data);
    },
    getMagazinePage: async ({ commit }, payload: { publicationId: string, page: number }): Promise<string> => {
        const response = await magazineRepository.getMagazinePage(payload);
        const objectUrl = URL.createObjectURL(response.data);
        const image = new Image();
        image.src = objectUrl;
        commit('saveMagazineImage', new MagazineImage({
            publicationId: payload.publicationId,
            page: payload.page,
            image: image
        }));
        return objectUrl;
    },
    applyFilterSearchText: ({ commit, getters }, searchText: string): void => {
        commit('setFilterSearchText', searchText);
        commit('applyFilter');
        if (getters.pagination) {
            commit('applyPagination', 0);
        }
    },
    applyFilterLanguage: ({ commit, getters }, languages: string[]): void => {
        commit('setFilterLanguages', languages);
        commit('applyFilter');
        if (getters.pagination) {
            commit('applyPagination', 0);
        }
    },
    applyFilterCategory: ({ commit, getters }, category?: Category): void => {
        commit('setFilterCategory', category);
        commit('applyFilter');
        if (getters.pagination) {
            commit('applyPagination', 0);
        }
    }
};

const mutations: MutationTree<MagazineState> = {
    setPaginationIndex: (state: MagazineState, value: number) => {
        state.paginationIndex = value;
    },
    setPagination: (state: MagazineState, value: number | null) => {
        state.pagination = value;
    },
    storeMagazines: (state: MagazineState, magazines: Magazine[]) => {
        state.magazines = magazines;
    },
    storeCategories: (state: MagazineState, categories: Category[]) => {
        state.categories = categories;
    },
    setLoadingMagazines: (state: MagazineState, isLoading: boolean) => {
        state.isLoadingMagazines = isLoading;
    },
    setIsInitialLoadingDone: (state: MagazineState, value: boolean) => {
        state.isInitialLoadingDone = value;
    },
    setSelectedMagazine: (state: MagazineState, selectedMagazine: Magazine) => {
        state.selectedMagazine = selectedMagazine;
    },
    resetSelectedMagazine: (state: MagazineState) => {
        state.selectedMagazine = undefined;
    },
    setLanguages: (state: MagazineState, magazines: Magazine[]) => {
        const languages = magazines.map(magazine => magazine.language);
        // remove duplicates from array
        state.languages = [...new Set(languages)];
    },
    setFilterSearchText: (state: MagazineState, searchText: string) => {
        state.filter.searchText = searchText;
    },
    setFilterLanguages: (state: MagazineState, languages: string[]) => {
        state.filter.languages = languages;
    },
    setFilterCategory: (state: MagazineState, category?: Category) => {
        state.filter.category = category;
    },
    applyFilter: (state: MagazineState) => {
        state.filteredMagazines = [];

        // --- filter functions ---
        const searchTextFunction = (magazine: Magazine): boolean => magazine.name.toLowerCase().includes(state.filter.searchText.toLowerCase());
        // if filter.category is undefined return each magazine, otherwise filter for category
        const categoryFunction = (magazine: Magazine): boolean => state.filter.category ? magazine.categoryIds.includes(state.filter.category.id) : true;
        const languageFunction = (magazine: Magazine): boolean => state.filter.languages.includes('all') || state.filter.languages.includes(magazine.language);
        // sort alphabetically (further info to localeCompare:
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare)
        const sortFunction = (a: Magazine, b: Magazine) => a.name.localeCompare(b.name, i18n.locale);

        // use request animation frame to avoid laggy appearance animation in library
        requestAnimationFrame(() => {
            // if a filter searchText is available search category independent
            if (state.filter.searchText) {
                state.filteredMagazines = state.magazines
                    .filter(searchTextFunction)
                    .filter(languageFunction)
                    .sort(sortFunction);
            } else {
                state.filteredMagazines = state.magazines
                    .filter(categoryFunction)
                    .filter(languageFunction)
                    .sort(sortFunction);
            }
        });
    },
    applyPagination: (state: MagazineState, paginationIndex: number) => {
        // use request animation frame to avoid laggy appearance animation in library
        requestAnimationFrame(() => {
            // set new index
            state.paginationIndex = paginationIndex;
            // apply index
            const paginationStart = state.paginationIndex * state.pagination!;
            const paginationEnd = paginationStart + state.pagination!;
            state.paginatedMagazines = state.filteredMagazines
                .slice(paginationStart, paginationEnd);
        });
    },
    setCategoryMagazineCount: (state: MagazineState, magazines: Magazine[]) => {
        // -- set categoryMagazineCount
        const categoryMagazineCount: CategoryMagazineCount = {
            all: {
                total: 0
            }
        };
        magazines.forEach(magazine => {
            // increase total all count
            categoryMagazineCount.all.total++;
            // set language specific total counting
            categoryMagazineCount[magazine.language] = {
                total: 0,
                ...categoryMagazineCount[magazine.language]
            };
            // -- count for whole language
            categoryMagazineCount[magazine.language].total++;
            magazine.categoryIds.forEach(categoryId => {
                // increase all specific count
                // increase total all count
                categoryMagazineCount.all = {
                    [categoryId]: 0,
                    ...categoryMagazineCount.all // override total and [categoryId] if already initialized
                };
                // -- count per category
                categoryMagazineCount.all[categoryId]++;
                // increase language specific count
                categoryMagazineCount[magazine.language] = {
                    [categoryId]: 0,
                    ...categoryMagazineCount[magazine.language] // override total and [categoryId] if already initialized
                };
                // -- count per category
                categoryMagazineCount[magazine.language][categoryId]++;
            });
        });
        state.categoryMagazineCount = categoryMagazineCount;
    },
    saveMagazineImage: (state: MagazineState, magazineImage: MagazineImage) => {
        const imageIndex = state.images.findIndex((imageObject) =>
            imageObject.publicationId === magazineImage.publicationId && imageObject.page === magazineImage.page);
        if (imageIndex !== -1) {
            state.images[imageIndex] = magazineImage;
        } else {
            state.images.push(magazineImage);
        }
    }
};

const getters: GetterTree<MagazineState, RootState> = {
    paginationMaxIndex: (state: MagazineState) => Math.ceil(state.filteredMagazines.length / state.pagination!),
    paginationIndex: (state: MagazineState) => state.paginationIndex,
    pagination: (state: MagazineState) => state.pagination,
    magazines: (state: MagazineState) => state.magazines,
    filteredMagazines: (state: MagazineState) => state.filteredMagazines,
    paginatedMagazines: (state: MagazineState) => state.paginatedMagazines,
    categories: (state: MagazineState) => state.categories,
    languages: (state: MagazineState) => state.languages,
    isLoadingMagazines: (state: MagazineState) => state.isLoadingMagazines,
    isInitialLoadingDone: (state: MagazineState) => state.isInitialLoadingDone,
    selectedMagazine: (state: MagazineState) => state.selectedMagazine,
    filterValues: (state: MagazineState) => state.filter,
    categoryMagazineCount: (state: MagazineState) => state.categoryMagazineCount,
    getCachedImageByIdAndPage: (state) => (publicationId: string, page: number) => {
        return state.images.find(imageObject => imageObject.publicationId === publicationId && imageObject.page === page);
    }
};

const magazineStore: Module<MagazineState, RootState> = {
    state: store,
    actions,
    mutations,
    getters
};

export default magazineStore;
